/**
 * PublicBookingPage — Conversational Agentic Chat
 * ──────────────────────────────────────────────────────────────────────
 * Patient-facing booking link. Ground-up rewrite to a TRUE chat interface
 * driven by the /api/public/booking/{slug}/chat endpoint.
 *
 *   • Strict black / white / gray — no practice brand colour plumbing.
 *   • Zero emoji — icons come from `lucide-react` only.
 *   • "Powered by Aria One" footer.
 *   • Free-text + voice dictation. The AI extracts intent; the UI only
 *     renders what the server's `action` says to render (slots, chips,
 *     Lens, contact form, confirmation).
 *
 * Response-shape the frontend honours:
 *   { session_id, reply, action, data, context }
 * Where `action` ∈ clarify | ask_new_or_existing | show_slots |
 *                  trigger_lens | collect_contact | confirm_booking.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle2, Phone, MapPin, Lock, Loader2, Mic, MicOff,
  ScanLine, Send, RotateCcw, ShieldCheck, Sparkles, Calendar, Clock,
  UserPlus, UserCheck, IdCard, Camera,
} from 'lucide-react';
import { useAppMode } from '../../contexts/AppModeContext';

const API = process.env.REACT_APP_BACKEND_URL;
const ARIAONE_LOGO = '/ariaone_logo_black.png';

/* ════════════════════════════════════════════════════════════════════
   Locked visual tokens. Patient-facing pages intentionally ignore any
   practice colour customisation.
   ════════════════════════════════════════════════════════════════════ */
const TOKENS = {
  page: '#F6F6F4',
  surface: '#FFFFFF',
  border: '#E8E5E0',
  text: '#1A1A1A',
  text2: '#5C5C5C',
  text3: '#9A9A9A',
  accent: '#111111',
  accentBg: '#F1F1F1',
  success: '#1D8A5E',
};

/* ────────── Chat bubbles ────────── */
const EchoBubble = ({ initials, children }) => (
  <div className="flex items-end gap-2" data-testid="echo-bubble">
    <div className="h-7 w-7 rounded-full bg-slate-900 text-white inline-flex items-center justify-center text-[10px] font-semibold shrink-0">
      {initials}
    </div>
    <div className="max-w-[82%] px-3.5 py-2.5 rounded-2xl rounded-bl-sm bg-slate-100 text-slate-900 text-[14px] leading-relaxed">
      {children}
    </div>
  </div>
);

const PatientBubble = ({ children }) => (
  <div className="flex justify-end" data-testid="patient-bubble">
    <div className="max-w-[82%] px-3.5 py-2.5 rounded-2xl rounded-br-sm bg-slate-900 text-white text-[14px] leading-relaxed">
      {children}
    </div>
  </div>
);

const TypingDots = () => (
  <div className="flex items-end gap-2" data-testid="typing-dots">
    <div className="h-7 w-7 rounded-full bg-slate-900" />
    <div className="flex items-center gap-1 px-3 py-2.5 rounded-2xl rounded-bl-sm bg-slate-100 w-14">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: '0s' }} />
      <span className="h-1.5 w-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: '0.12s' }} />
      <span className="h-1.5 w-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: '0.24s' }} />
    </div>
  </div>
);

/* ────────── Inline action cards (rendered below assistant message) ────────── */
const ChipRow = ({ chips, onPick, testid }) => (
  <div className="flex flex-wrap gap-2 pl-9" data-testid={testid}>
    {chips.map((c) => (
      <button
        key={c.value || c.label}
        type="button"
        onClick={() => onPick(c)}
        className="px-3 py-1.5 rounded-full border border-slate-300 bg-white text-[13px] text-slate-900 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition"
        data-testid={`chip-${(c.value || c.label).toString().toLowerCase().replace(/\s+/g, '-')}`}
      >
        {c.label}
      </button>
    ))}
  </div>
);

const NewOrExistingCard = ({ onPick }) => (
  <div className="pl-9 grid grid-cols-2 gap-2 max-w-md" data-testid="new-existing-card">
    <button
      type="button"
      onClick={() => onPick('new_patient')}
      className="p-3 rounded-xl border border-slate-300 bg-white text-left hover:border-slate-900 transition"
      data-testid="chip-new-patient"
    >
      <UserPlus size={16} className="mb-1.5 text-slate-900" />
      <div className="text-[13px] font-semibold text-slate-900">New patient</div>
      <div className="text-[11px] text-slate-500">First time here</div>
    </button>
    <button
      type="button"
      onClick={() => onPick('existing_patient')}
      className="p-3 rounded-xl border border-slate-300 bg-white text-left hover:border-slate-900 transition"
      data-testid="chip-existing-patient"
    >
      <UserCheck size={16} className="mb-1.5 text-slate-900" />
      <div className="text-[13px] font-semibold text-slate-900">Existing patient</div>
      <div className="text-[11px] text-slate-500">Seen here before</div>
    </button>
  </div>
);

const SlotsCard = ({ slots, onPick }) => (
  <div className="pl-9 space-y-1.5 max-w-md" data-testid="slots-card">
    {slots.map((s) => (
      <button
        key={s.id}
        type="button"
        onClick={() => onPick(s)}
        className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-left hover:border-slate-900 hover:bg-slate-50 transition"
        data-testid={`slot-${s.id}`}
      >
        <span className="inline-flex items-center gap-2 text-[13px] text-slate-900">
          <Clock size={13} className="text-slate-500" />
          {s.label}
        </span>
        <span className="text-[11px] text-slate-500">Pick</span>
      </button>
    ))}
  </div>
);

/* Mock Lens capture — real impl would invoke camera / document scan. */
const LensCard = ({ onDone }) => {
  const [id, setId] = useState(false);
  const [ins, setIns] = useState(false);
  const [busy, setBusy] = useState(null);
  const snap = (which) => {
    setBusy(which);
    setTimeout(() => {
      if (which === 'id') setId(true);
      if (which === 'ins') setIns(true);
      setBusy(null);
    }, 900);
  };
  return (
    <div className="pl-9 max-w-md" data-testid="lens-card">
      <div className="rounded-xl border border-slate-300 bg-white p-3.5 space-y-3">
        <div className="flex items-center gap-2 text-[12px] text-slate-500">
          <ScanLine size={14} /> Aria One Lens · on-device capture
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => snap('id')}
            disabled={busy === 'id' || id}
            className={`p-3 rounded-lg border text-left transition ${
              id ? 'border-slate-900 bg-slate-50' : 'border-slate-300 bg-white hover:border-slate-900'
            } disabled:opacity-60`}
            data-testid="lens-capture-id"
          >
            <IdCard size={16} className="mb-1.5 text-slate-900" />
            <div className="text-[13px] font-semibold text-slate-900">Driver's license</div>
            <div className="text-[11px] text-slate-500">
              {id ? 'Captured' : busy === 'id' ? 'Scanning…' : 'Front & back'}
            </div>
          </button>
          <button
            type="button"
            onClick={() => snap('ins')}
            disabled={busy === 'ins' || ins}
            className={`p-3 rounded-lg border text-left transition ${
              ins ? 'border-slate-900 bg-slate-50' : 'border-slate-300 bg-white hover:border-slate-900'
            } disabled:opacity-60`}
            data-testid="lens-capture-insurance"
          >
            <Camera size={16} className="mb-1.5 text-slate-900" />
            <div className="text-[13px] font-semibold text-slate-900">Insurance card</div>
            <div className="text-[11px] text-slate-500">
              {ins ? 'Captured' : busy === 'ins' ? 'Scanning…' : 'Front only'}
            </div>
          </button>
        </div>
        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            onClick={() => onDone({ captured: false })}
            className="text-[12px] text-slate-500 hover:text-slate-900 underline underline-offset-2"
            data-testid="lens-skip"
          >
            Skip — I'll bring them
          </button>
          <button
            type="button"
            onClick={() => onDone({ captured: id || ins, id, ins })}
            disabled={!id && !ins}
            className="px-3 py-1.5 rounded-full bg-slate-900 text-white text-[12px] font-medium disabled:opacity-40"
            data-testid="lens-continue"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

const ContactCard = ({ onSubmit }) => {
  const [f, setF] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  const ok = f.first_name.trim() && f.last_name.trim() && (f.phone.trim() || f.email.trim());
  return (
    <div className="pl-9 max-w-md" data-testid="contact-card">
      <div className="rounded-xl border border-slate-300 bg-white p-3.5 space-y-2.5">
        <div className="grid grid-cols-2 gap-2">
          <input
            className="px-3 py-2.5 rounded-lg border border-slate-300 text-[14px] outline-none focus:border-slate-900"
            placeholder="First name"
            value={f.first_name}
            onChange={(e) => setF({ ...f, first_name: e.target.value })}
            data-testid="contact-first-name"
          />
          <input
            className="px-3 py-2.5 rounded-lg border border-slate-300 text-[14px] outline-none focus:border-slate-900"
            placeholder="Last name"
            value={f.last_name}
            onChange={(e) => setF({ ...f, last_name: e.target.value })}
            data-testid="contact-last-name"
          />
        </div>
        <input
          className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-[14px] outline-none focus:border-slate-900"
          placeholder="Mobile phone"
          value={f.phone}
          onChange={(e) => setF({ ...f, phone: e.target.value })}
          data-testid="contact-phone"
        />
        <input
          className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-[14px] outline-none focus:border-slate-900"
          placeholder="Email (optional)"
          value={f.email}
          onChange={(e) => setF({ ...f, email: e.target.value })}
          data-testid="contact-email"
        />
        <div className="flex items-center justify-between pt-1">
          <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
            <Lock size={11} /> HIPAA-aware · encrypted
          </span>
          <button
            type="button"
            disabled={!ok}
            onClick={() => onSubmit(f)}
            className="px-4 py-2 rounded-full bg-slate-900 text-white text-[13px] font-medium disabled:opacity-40"
            data-testid="contact-submit"
          >
            Confirm booking
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmCard = ({ data, onReset }) => (
  <div className="pl-9 max-w-md" data-testid="confirm-card">
    <div className="rounded-xl border border-slate-300 bg-white p-4 space-y-3">
      <div className="inline-flex items-center gap-2 text-slate-900">
        <CheckCircle2 size={16} />
        <span className="text-[14px] font-semibold">Booking received</span>
      </div>
      <div className="text-[13px] text-slate-600 space-y-1">
        {data?.slot?.label && <div><span className="text-slate-400">Time · </span>{data.slot.label}</div>}
        {data?.visit_type && <div><span className="text-slate-400">Visit · </span>{data.visit_type}</div>}
        {data?.provider && <div><span className="text-slate-400">Provider · </span>{data.provider}</div>}
        {data?.request_id && <div><span className="text-slate-400">Ref · </span>{data.request_id}</div>}
      </div>
      <div className="text-[12px] text-slate-500">
        We'll text a confirmation. If you need to change or cancel, reply to that text.
      </div>
      <button
        type="button"
        onClick={onReset}
        className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-900 underline underline-offset-2"
        data-testid="confirm-restart"
      >
        <RotateCcw size={12} /> Book another visit
      </button>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Main component
   ══════════════════════════════════════════════════════════════════════ */
const PublicBookingPage = () => {
  const { isDemo } = useAppMode();
  const { slug } = useParams();
  const [config, setConfig] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  // Unified message log. Each entry: { role, text, action?, data?, id }
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [currentAction, setCurrentAction] = useState(null); // {action, data, mid}
  const [composerText, setComposerText] = useState('');
  const [listening, setListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [inputLocked, setInputLocked] = useState(false);

  const threadRef = useRef(null);
  const recogRef = useRef(null);

  const initials = useMemo(() => {
    const n = config?.branding?.practice_name || 'Aria';
    return n.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || 'A';
  }, [config]);

  /* ── Bootstrap: load widget config, then kick off greeting ── */
  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/booking/${slug}`);
        if (!on) return;
        setConfig(data);
      } catch (e) {
        if (!on) return;
        setLoadError(e?.response?.status === 404
          ? 'This booking link is no longer active.'
          : 'Unable to load this booking page.');
      }
    })();
    return () => { on = false; };
  }, [slug]);

  useEffect(() => {
    if (!config || messages.length) return;
    // First call — server greets.
    postToChat({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  /* ── Voice dictation (browser SpeechRecognition) ── */
  useEffect(() => {
    const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) return;
    setSpeechSupported(true);
    const r = new SR();
    r.lang = 'en-US';
    r.interimResults = true;
    r.maxAlternatives = 1;
    r.onresult = (e) => {
      let t = '';
      for (let i = e.resultIndex; i < e.results.length; i++) t += e.results[i][0].transcript;
      setComposerText(t);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recogRef.current = r;
    return () => { try { r.stop(); } catch (_) {} };
  }, []);

  const toggleMic = () => {
    const r = recogRef.current;
    if (!r) return;
    if (listening) { try { r.stop(); } catch (_) {} setListening(false); }
    else { try { r.start(); setListening(true); } catch (_) {} }
  };

  /* ── Auto-scroll to newest message ── */
  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, typing, currentAction]);

  /* ── Chat POST with optimistic typing indicator ── */
  const postToChat = async ({ message, event, event_data }) => {
    setTyping(true);
    setInputLocked(true);
    try {
      const { data } = await axios.post(`${API}/api/public/booking/${slug}/chat`, {
        session_id: sessionId,
        message,
        event,
        event_data,
      });
      if (data.session_id && !sessionId) setSessionId(data.session_id);
      const mid = `m_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      setMessages((prev) => [...prev, {
        id: mid,
        role: 'assistant',
        text: data.reply,
        action: data.action,
        data: data.data,
      }]);
      // "Active" action is the last one the server asked us to render.
      setCurrentAction({ action: data.action, data: data.data, mid });
    } catch (e) {
      setMessages((prev) => [...prev, {
        id: `err_${Date.now()}`,
        role: 'assistant',
        text: 'Sorry — something went wrong. Please try again.',
        action: 'clarify',
        data: {},
      }]);
    } finally {
      setTyping(false);
      setInputLocked(false);
    }
  };

  const sendUserText = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return;
    setMessages((prev) => [...prev, { id: `u_${Date.now()}`, role: 'user', text: trimmed }]);
    setComposerText('');
    // Clear any open action card — user has moved on.
    setCurrentAction(null);
    await postToChat({ message: trimmed });
  };

  const sendEvent = async (event, event_data, userLabel) => {
    if (userLabel) {
      setMessages((prev) => [...prev, { id: `u_${Date.now()}`, role: 'user', text: userLabel }]);
    }
    setCurrentAction(null);
    await postToChat({ event, event_data });
  };

  const handleSuggestionPick = (c) => sendUserText(c.value);
  const handleNewExisting = (ev) =>
    sendEvent(ev, {}, ev === 'new_patient' ? 'New patient' : 'Existing patient');
  const handleSlotPick = (s) => sendEvent('slot_pick', { id: s.id, label: s.label }, s.label);
  const handleLensDone = (d) => sendEvent('lens_done', d, d.captured ? 'Captured ID / insurance' : "Skipped — I'll bring them");
  const handleContactSubmit = (f) =>
    sendEvent('contact_done', f, `${f.first_name} ${f.last_name}`.trim());
  const handleReset = () => {
    setMessages([]);
    setCurrentAction(null);
    sendEvent('restart', {}, null);
  };

  /* ── Error / loading states ── */
  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: TOKENS.page }}>
        <div className="max-w-md text-center">
          <div className="text-[15px] font-semibold text-slate-900 mb-1">Link not available</div>
          <div className="text-[13px] text-slate-500">{loadError}</div>
        </div>
      </div>
    );
  }
  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: TOKENS.page }}>
        <Loader2 className="animate-spin text-slate-500" size={22} />
      </div>
    );
  }

  const branding = config.branding || {};
  const renderActionCard = () => {
    if (!currentAction) return null;
    const { action, data, mid } = currentAction;
    // Hide the card if a newer message has displaced it.
    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
    if (lastAssistant && lastAssistant.id !== mid) return null;

    switch (action) {
      case 'clarify':
        if (Array.isArray(data?.suggestions) && data.suggestions.length) {
          return <ChipRow chips={data.suggestions} onPick={handleSuggestionPick} testid="clarify-chips" />;
        }
        return null;
      case 'ask_new_or_existing':
        return <NewOrExistingCard onPick={handleNewExisting} />;
      case 'show_slots':
        return <SlotsCard slots={data?.slots || []} onPick={handleSlotPick} />;
      case 'trigger_lens':
        return <LensCard onDone={handleLensDone} />;
      case 'collect_contact':
        return <ContactCard onSubmit={handleContactSubmit} />;
      case 'confirm_booking':
        return <ConfirmCard data={data} onReset={handleReset} />;
      default:
        return null;
    }
  };

  const doneAndLocked = currentAction?.action === 'confirm_booking';

  return (
    <div className="min-h-screen flex flex-col" style={{ background: TOKENS.page, color: TOKENS.text }}>
      {/* Header */}
      <header className="px-5 py-5 text-center">
        <div className="inline-flex items-center gap-2 mb-2 text-[11px] tracking-wider text-slate-500 uppercase">
          <Sparkles size={12} /> Agentic booking
        </div>
        <h1 className="text-[22px] sm:text-[26px] font-semibold text-slate-900">{branding.practice_name}</h1>
        {branding.tagline && (
          <p className="text-[13px] text-slate-500 mt-1">{branding.tagline}</p>
        )}
      </header>

      {/* Thread */}
      <main className="flex-1 flex flex-col max-w-2xl w-full mx-auto px-4 pb-6">
        <div
          ref={threadRef}
          className="flex-1 overflow-y-auto rounded-2xl bg-white border border-slate-200 shadow-sm"
          style={{ maxHeight: 'calc(100vh - 320px)', minHeight: 380 }}
          data-testid="chat-thread"
        >
          <div className="px-4 py-5 space-y-4">
            {messages.map((m) => (
              m.role === 'assistant' ? (
                <div key={m.id} className="space-y-2">
                  <EchoBubble initials={initials}>{m.text}</EchoBubble>
                </div>
              ) : (
                <PatientBubble key={m.id}>{m.text}</PatientBubble>
              )
            ))}
            {typing && <TypingDots />}
            {!typing && renderActionCard()}
          </div>
        </div>

        {/* Composer */}
        <form
          onSubmit={(e) => { e.preventDefault(); if (!doneAndLocked) sendUserText(composerText); }}
          className="mt-3 flex items-center gap-2"
          data-testid="chat-composer"
        >
          <div className="flex-1 flex items-center gap-2 rounded-full border border-slate-300 bg-white pl-4 pr-1 py-1">
            <input
              value={composerText}
              onChange={(e) => setComposerText(e.target.value)}
              placeholder={doneAndLocked
                ? "Book another to continue…"
                : (isDemo ? "Tell Echo what you need (e.g. I'm sick, need Dr. Chen tonight)" : "Tell Echo what you need…")}
              className="flex-1 bg-transparent outline-none text-[14px] py-2"
              disabled={inputLocked || doneAndLocked}
              data-testid="chat-input"
            />
            {speechSupported && !doneAndLocked && (
              <button
                type="button"
                onClick={toggleMic}
                title={listening ? 'Stop' : 'Voice'}
                className={`h-8 w-8 inline-flex items-center justify-center rounded-full ${listening ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                data-testid="chat-mic-btn"
              >
                {listening ? <MicOff size={15} /> : <Mic size={15} />}
              </button>
            )}
            <button
              type="submit"
              disabled={inputLocked || doneAndLocked || !composerText.trim()}
              className="h-9 px-3 inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-[13px] font-medium disabled:opacity-40"
              data-testid="chat-send-btn"
            >
              <Send size={13} /> Send
            </button>
          </div>
        </form>

        <div className="mt-2 text-center text-[11px] text-slate-400 inline-flex items-center gap-1.5 justify-center">
          <Lock size={10} /> HIPAA-aware — we never share your details without consent.
        </div>
      </main>

      {/* Footer */}
      <footer className="px-5 pb-8">
        <div className="max-w-2xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-[12px] text-slate-500">
          {branding.footer_phone && (
            <span className="inline-flex items-center gap-1.5"><Phone size={11} /> {branding.footer_phone}</span>
          )}
          {branding.footer_address && (
            <span className="inline-flex items-center gap-1.5"><MapPin size={11} /> {branding.footer_address}</span>
          )}
        </div>
        <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-slate-500">
          <ShieldCheck size={11} /> Powered by
          <img src={ARIAONE_LOGO} alt="Aria One" className="h-3.5 inline-block opacity-80" />
        </div>
      </footer>
    </div>
  );
};

export default PublicBookingPage;
